import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useParams} from "react-router";
import useStepperStateCloseValidator from "hooks/useStepperStateCloseValidator";
import RouterPath from "libs/RouterPath";
import useSnackbar from "hooks/useSnackbar";
import Tr from "translations/Translations"
import PurchasingGroupStage1Service from "services/PurchasingGroupStage1Service";
import Loading from "components/Loading";
import Form from "components/Form";
import VS from "constants/ValidationShema";
import SaveFormikValues from "components/SaveFormikValues";
import PurchasingGroupStage1ScheduleForm from "./PurchasingGroupStage1ScheduleForm";
import moment from "moment";
import PurchasingGroupSectionService from "services/PurchasingGroupSectionService";
import DemandValidationMessages from "components/DemandValidationMessages";
import scheduleReloadTimeout from "../../../utils/scheduleReloadTimeout";
import {PurchasingGroupStageSections} from "../../../constants/PurchasingGroupStageSections";

function PurchasingGroupStage1Schedule(
    {
        panelRef,
        validationSection
    }) {

    const params = useParams();
    const groupId = params.id;
    const formId = params.section;
    const editable = validationSection.editable;
    const {enqueueSuccessSnackbar, enqueueErrorSnackbar} = useSnackbar();
    const [schedule, setSchedule] = useState(null);
    const timeout = useRef(0);

    useStepperStateCloseValidator(formId);

    const closeSection = useCallback(() => RouterPath.redirectWithoutHistory(RouterPath.createPath(RouterPath.purchasingGroup.MP_PURCHASING_GROUP_GROUP_ID_STAGE_NR, {
        id: groupId,
        stage: 1
    })), [groupId]);

    const getSchedule = () => {
        setSchedule(null);
        if (timeout.current) {
            clearTimeout(timeout.current);
            timeout.current = 0;
        }
        PurchasingGroupStage1Service.getSchedule(groupId).then((resp) => {
            if (resp) {
                setSchedule(resp);
                let reloadTimeout = scheduleReloadTimeout(resp.schedule, resp.serverTime);
                if (reloadTimeout) {
                    timeout.current = setTimeout(() => {
                        getSchedule();
                    }, reloadTimeout)
                }

            } else
                closeSection();
        });
    }

    useEffect(() => {
        getSchedule();
        PurchasingGroupSectionService.getSections(groupId, validationSection.section);
        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
                timeout.current = 0;
            }
        }
        // eslint-disable-next-line
    }, [groupId])


    const handleSubmit = (values) => {
        PurchasingGroupStage1Service.saveSchedule(groupId, values).then((resp) => {
            if (resp && resp.success === false) {
                Object.keys(resp.errors).forEach((field) => {
                    let error;
                    switch (field) {
                        case "joiningStartDate":
                            error = `${Tr('purchasing_group_joining_stage')} ${Tr('purchase_group_stage_end_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "joiningEndDate":
                            error = `${Tr('purchasing_group_joining_stage')}: ${Tr('purchase_group_stage_start_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "opinionStartDate":
                            error = `${Tr('purchasing_group_opinion_stage')}: ${Tr('purchase_group_stage_end_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "opinionEndDate":
                            error = `${Tr('purchasing_group_opinion_stage')}: ${Tr('purchase_group_stage_start_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "submissionStartDate":
                            error = `${Tr('purchasing_group_submission_stage')}: ${Tr('purchase_group_stage_start_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        case "submissionEndDate":
                            error = `${Tr('purchasing_group_submission_stage')}: ${Tr('purchase_group_stage_start_date')}: ${resp.errors[field].join(", ")}`;
                            break;
                        default:
                            error = `${resp.errors[field].join(", ")}`
                    }
                    enqueueErrorSnackbar(error)
                })
            } else if(resp) {
                enqueueSuccessSnackbar(Tr("saved") + " " + validationSection.name);
                PurchasingGroupSectionService.getSections(groupId, [validationSection.section, PurchasingGroupStageSections.STAGE_1_SECTION])
                closeSection();
            }
        }).catch((e) => enqueueErrorSnackbar(e.message))
    }

    if (!schedule) return <Loading/>

    return (<>
            <DemandValidationMessages editable={validationSection.editable} section={validationSection}/>
            <Form
                id={formId}
                initialValues={{
                    joiningStartDate: schedule.schedule.joiningStartDate ? moment(schedule.schedule.joiningStartDate) : null,
                    joiningEndDate: schedule.schedule.joiningEndDate ? moment(schedule.schedule.joiningEndDate) : null,
                    opinionStartDate: schedule.schedule.opinionStartDate ? moment(schedule.schedule.opinionStartDate) : null,
                    opinionEndDate: schedule.schedule.opinionEndDate ? moment(schedule.schedule.opinionEndDate) : null,
                    submissionStartDate: schedule.schedule.submissionStartDate ? moment(schedule.schedule.submissionStartDate) : null,
                    submissionEndDate: schedule.schedule.submissionEndDate ? moment(schedule.schedule.submissionEndDate) : null
                }}
                validationSchema={VS.lazy((form) => {

                    const shape = {}
                    if (schedule.joiningStartDateEditable) {
                        shape.joiningStartDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'))
                    }
                    if (schedule.joiningEndDateEditable) {
                        const joiningStartDate = form.joiningStartDate ? form.joiningStartDate.clone() : moment();
                        joiningStartDate.add(1, "minute")
                        shape.joiningEndDate = VS.dateTimeMin(joiningStartDate, Tr('v_end_time_can_not_be_before_start'))
                    }

                    if (schedule.opinionStartDateEditable) {
                        if (schedule.joiningEndDateEditable) {
                            shape.opinionStartDate = VS.dateTimeMinNoRq(new Date(), Tr('v_time_can_not_be_past'))
                        } else {
                            shape.opinionStartDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'))
                        }
                    }
                    if (schedule.opinionEndDateEditable) {
                        const opinionStartDate = form.opinionStartDate ? form.opinionStartDate.clone() : moment();
                        opinionStartDate.add(1, "minute")

                        if (schedule.joiningEndDateEditable && !form.opinionStartDate) {
                            shape.opinionEndDate = VS.dateTimeMinNoRq(opinionStartDate, Tr('v_end_time_can_not_be_before_start'))
                        } else {
                            shape.opinionEndDate = VS.dateTimeMin(opinionStartDate, Tr('v_end_time_can_not_be_before_start'))
                        }
                    }

                    if (schedule.submissionStartDateEditable) {
                        if (schedule.opinionEndDateEditable) {
                            shape.submissionStartDate = VS.dateTimeMinNoRq(new Date(), Tr('v_time_can_not_be_past'))
                        } else {
                            shape.submissionStartDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'))
                        }
                    }
                    if (schedule.submissionEndDateEditable) {
                        const submissionStartDate = form.submissionStartDate ? form.submissionStartDate.clone() : moment();
                        submissionStartDate.add(1, "minute")

                        if (schedule.opinionEndDateEditable && !form.submissionStartDate) {
                            shape.submissionEndDate = VS.dateTimeMinNoRq(submissionStartDate, Tr('v_end_time_can_not_be_before_start'))
                        } else {
                            shape.submissionEndDate = VS.dateTimeMin(submissionStartDate, Tr('v_end_time_can_not_be_before_start'))
                        }
                    }

                    return VS.object(shape)
                })}
                onSubmit={handleSubmit}
            >
                {(formikProps) => (
                    <>
                        <SaveFormikValues
                            formikProps={formikProps}
                            id={formId}
                        />
                        <PurchasingGroupStage1ScheduleForm editable={editable} schedule={schedule} formikProps={formikProps}/>
                        {/*<Typography style={{lineBreak: "anywhere"}}>{JSON.stringify(schedule)}</Typography>*/}
                    </>)}
            </Form>
        </>
    )
}

PurchasingGroupStage1Schedule.propTypes = {}

export default PurchasingGroupStage1Schedule;